import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertOctagon, ArrowLeft, CalendarClock, Camera, CheckCircle2, Copy, ExternalLink, ImagePlus, Loader2, Package, Plus, RotateCcw, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AjoutColisDialog } from "@/components/colis/AjoutColisDialog";
import { InstanceDialog } from "@/components/colis/InstanceDialog";
import { PhotosGrille } from "@/components/colis/PhotosColis";
import { ChoixDate, EcheanceBadge } from "@/components/colis/Echeance";
import { DEADLINE_MAX_JOURS, TYPES_DATE, aujourdhui, fmtJour, lundiProchain, plusJours, raccourcisDeadline } from "@/lib/echeance";
import { envoyerPhoto } from "@/lib/photos";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";
import { api, errMsg } from "@/lib/api";
import { lienSuivi, numeroLisible } from "@/lib/colisCode";
import { StatutColis } from "./ColisPage";
import { colisSubnav } from "@/lib/subnavs";
import { useDouchette } from "@/lib/douchette";
import { CapteurLecteur } from "@/components/colis/ChampLecteur";

function Ligne({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-4 py-2.5 border-b border-border last:border-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground sm:w-40 shrink-0">{label}</dt>
      <dd className="text-sm min-w-0">{children}</dd>
    </div>
  );
}

function InstanceCarte({ colis, inst, photos, user, onChange }) {
  const [resoudre, setResoudre] = useState(false);
  const [comm, setComm] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [report, setReport] = useState(false);
  const [jour, setJour] = useState(inst.date || "");
  const [commReport, setCommReport] = useState("");
  const ouverte = inst.statut === "ouverte";
  const td = TYPES_DATE[inst.type_date];
  const raccourcis = inst.type_date === "remise"
    ? [["Demain", plusJours(1)], ["Après-demain", plusJours(2)], ["Lundi prochain", lundiProchain()]]
    : raccourcisDeadline();
  const maxDate = inst.type_date === "limite" ? plusJours(DEADLINE_MAX_JOURS) : undefined;

  const changerDate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/colis/${colis.id}/instances/${inst.id}/date`, { date: jour, commentaire: commReport });
      toast.success(`${td.label} : ${fmtJour(jour)}`);
      setReport(false); setCommReport("");
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const changer = async (statut) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/colis/${colis.id}/instances/${inst.id}`, { statut, commentaire: comm });
      toast.success(statut === "resolue" ? "Instance résolue" : "Instance rouverte");
      setResoudre(false); setComm("");
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const supprimer = async () => {
    try { const { data } = await api.delete(`/colis/${colis.id}/instances/${inst.id}`); toast.success("Instance supprimée"); onChange(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <li className={`rounded-lg border p-3 ${ouverte ? "border-amber-500/40 bg-amber-500/5" : "border-border"}`} data-testid={`instance-${inst.motif}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm flex items-center gap-1.5">
            {ouverte ? <AlertOctagon size={14} className="text-amber-500 shrink-0" /> : <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />}
            {inst.motif_label}
          </p>
          <p className="text-xs text-muted-foreground">{fmtDateHeure(inst.cree_le)} · {inst.par?.nom || "—"} ({inst.par?.matricule || "—"})</p>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ouverte ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"}`}>
          {ouverte ? "En cours" : "Résolue"}
        </span>
      </div>
      {td && inst.date && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid="instance-date-ligne">
          <span className="text-muted-foreground">{td.label} :</span>
          <span className={`font-semibold ${inst.type_date === "limite" ? "text-red-600 dark:text-red-400" : ""}`}>{fmtJour(inst.date)}</span>
          {ouverte && <EcheanceBadge e={inst.echeance} court />}
          {inst.dates_precedentes?.length > 0 && (
            <span className="text-xs text-muted-foreground">(reportée {inst.dates_precedentes.length} fois, initialement le {fmtJour(inst.dates_precedentes[0].date)})</span>
          )}
        </div>
      )}
      {inst.commentaire && <p className="text-sm mt-2 whitespace-pre-line">{inst.commentaire}</p>}
      {photos.length > 0 && <div className="mt-2"><PhotosGrille colis={colis} photos={photos} user={user} size="sm" onChange={() => onChange()} /></div>}
      {!ouverte && (
        <p className="text-xs text-muted-foreground mt-2">
          Résolue le {fmtDateHeure(inst.resolue_le)} par {inst.resolue_par?.nom || "—"}{inst.commentaire_resolution ? ` — « ${inst.commentaire_resolution} »` : ""}
        </p>
      )}
      {report ? (
        <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
          <p className="text-sm font-semibold">Changer : {td?.label.toLowerCase()}</p>
          <ChoixDate type={inst.type_date} value={jour} onChange={setJour} raccourcis={raccourcis} min={aujourdhui()} max={maxDate} testId="report-date" />
          <Textarea value={commReport} onChange={(e) => setCommReport(e.target.value)} rows={2} placeholder="Raison (facultatif) — ex. destinataire rappelé, nouvelle adresse attendue" />
          <div className="flex gap-2">
            <Button size="sm" onClick={changerDate} disabled={busy || !jour || jour < aujourdhui() || (maxDate && jour > maxDate) || jour === inst.date} data-testid="report-valider">{busy && <Loader2 size={13} className="animate-spin" />} Valider la date</Button>
            <Button size="sm" variant="ghost" onClick={() => { setReport(false); setJour(inst.date || ""); }}>Annuler</Button>
          </div>
        </div>
      ) : resoudre ? (
        <div className="mt-3 space-y-2">
          <Textarea value={comm} onChange={(e) => setComm(e.target.value)} rows={2} placeholder="Comment l'instance a été réglée (facultatif) — ex. livré le lendemain" data-testid="resolution-commentaire" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => changer("resolue")} disabled={busy} data-testid="resolution-valider">{busy && <Loader2 size={13} className="animate-spin" />} Valider la résolution</Button>
            <Button size="sm" variant="ghost" onClick={() => setResoudre(false)}>Annuler</Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1">
          {ouverte
            ? <>
              {peut(user, "colis_instance_resoudre") && <Button size="sm" variant="outline" onClick={() => setResoudre(true)} data-testid="btn-resoudre"><CheckCircle2 size={13} /> Marquer résolue</Button>}
              {td && peut(user, "colis_instance_date") && <Button size="sm" variant="outline" onClick={() => { setJour(inst.date || ""); setReport(true); }} data-testid="btn-report"><CalendarClock size={13} /> {inst.type_date === "remise" ? "Changer la date" : "Repousser la date limite"}</Button>}
            </>
            : peut(user, "colis_instance_resoudre") && <Button size="sm" variant="ghost" onClick={() => changer("ouverte")} disabled={busy}><RotateCcw size={13} /> Rouvrir</Button>}
          {peut(user, "colis_instance_supprimer") && (confirmDel
            ? <Button size="sm" variant="destructive" onClick={supprimer}>Supprimer l'instance ?</Button>
            : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(true)} title="Supprimer l'instance"><Trash2 size={13} /></Button>)}
        </div>
      )}
    </li>
  );
}

// Fichier d'inventaire du transporteur + emplacement au dépôt : lus à chaque
// affichage, donc à jour même si le fichier / l'inventaire sont venus après.
function Adresse({ titre, a, fort }) {
  if (!a) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{titre}</dt>
      <dd className={fort ? "font-semibold" : "font-medium"}>{a.rs || "—"}</dd>
      <dd className="text-sm">{[a.ad1, a.ad2].filter(Boolean).join(", ")}</dd>
      <dd className="text-sm">{[a.cp, a.ville].filter(Boolean).join(" ")}{a.pays && a.pays !== "FR" ? ` (${a.pays})` : ""}</dd>
    </div>
  );
}

function SuiviFichier({ c }) {
  const i = c.info;
  const d = c.depot;
  return (
    <section className="rounded-xl border border-border bg-card p-5" data-testid="colis-suivi">
      <h2 className="font-heading font-semibold">Fichier du transporteur</h2>
      {!i ? (
        <p className="text-sm text-muted-foreground mt-1" data-testid="colis-suivi-absent">Ce colis n'est pas dans le dernier fichier d'inventaire importé (Dépôt restant › Importer le fichier).</p>
      ) : (
        <dl className="text-sm space-y-3 mt-3">
          {i.numero && i.numero !== c.numero && (
            <div data-testid="colis-suivi-rapprochement"><dt className="text-xs text-muted-foreground">N° dans le fichier (étiquette TNT)</dt>
              <dd className="font-mono">{i.numero}</dd></div>
          )}
          <Adresse titre="Destinataire" a={i.destinataire} fort />
          <Adresse titre="Expéditeur" a={i.expediteur} />
          {(i.date_pec || i.client_facture) && (
            <div><dt className="text-xs text-muted-foreground">Prise en charge</dt>
              <dd>{i.date_pec ? new Date(i.date_pec).toLocaleDateString("fr-FR") : "—"}{i.client_facture ? ` · client ${i.client_facture}` : ""}</dd></div>
          )}
          {i.cr && <div><dt className="text-xs text-muted-foreground">Compte rendu distribution</dt><dd>{i.cr}{i.maj_le ? <span className="text-muted-foreground"> · {new Date(i.maj_le).toLocaleDateString("fr-FR")}</span> : null}</dd></div>}
        </dl>
      )}
      <div className="mt-4 pt-3 border-t border-border text-sm" data-testid="colis-suivi-depot">
        <p className="text-xs text-muted-foreground">Dépôt restant (inventaire du jour)</p>
        {d ? <p><b>{d.emplacement}</b> <span className="text-muted-foreground">· inventaire validé le {fmtDateHeure(d.inventaire_le)}</span></p>
          : <p className="text-muted-foreground">Pas sur un chariot dans l'inventaire du jour.</p>}
      </div>
      {c.cage && (
        <div className="mt-3 pt-3 border-t border-border text-sm" data-testid="colis-suivi-cage">
          <p className="text-xs text-muted-foreground">Colis cage (inventaire du jour)</p>
          <p><b>{c.cage.emplacement}</b> <span className="text-muted-foreground">· inventaire validé le {fmtDateHeure(c.cage.inventaire_le)}</span></p>
        </div>
      )}
    </section>
  );
}

export default function ColisFichePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [ajout, setAjout] = useState(false);
  // Lecteur intégré (Zebra) / douchette : scanner un colis ouvre « Scanner un colis » avec son n°.
  const [codeLu, setCodeLu] = useState(null);
  useDouchette((code) => { setCodeLu(code); setAjout("saisie"); }, !ajout);
  const [motifs, setMotifs] = useState(null);
  const [instanceOpen, setInstanceOpen] = useState(false);
  const [envoi, setEnvoi] = useState(null); // "n/total" pendant l'envoi de photos

  useEffect(() => {
    setC(null); setIntrouvable(false); setConfirmDel(false);
    api.get(`/colis/${id}`).then(({ data }) => setC(data)).catch((e) => {
      if (e?.response?.status === 404) setIntrouvable(true); else toast.error(errMsg(e));
    });
  }, [id]);
  useEffect(() => { api.get("/colis/motifs-instance").then(({ data }) => setMotifs(data)).catch(() => {}); }, []);

  const recharger = useCallback(async (data) => {
    if (data) { setC(data); return; }
    try { const r = await api.get(`/colis/${id}`); setC(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, [id]);

  const ajouterPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setEnvoi(`${i + 1}/${files.length}`);
      try { await envoyerPhoto(api, c.id, files[i]); ok++; } catch (err) { toast.error(errMsg(err)); }
    }
    setEnvoi(null);
    if (ok) toast.success(`${ok} photo(s) ajoutée(s)`);
    recharger();
  };

  const copier = async () => {
    try { await navigator.clipboard.writeText(c.numero); toast.success("Numéro copié"); } catch { /* presse-papiers indisponible */ }
  };
  const supprimer = async () => {
    try { await api.delete(`/colis/${c.id}`); toast.success("Fiche supprimée"); navigate("/colis"); } catch (e) { toast.error(errMsg(e)); }
  };
  const suivi = lienSuivi(c);

  return (
    <AppShell title="Fiche colis" subnav={colisSubnav()}>
      <CapteurLecteur onScan={(code) => { setCodeLu(code); setAjout("saisie"); }} actif={!ajout} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <Link to="/colis" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Tous les colis</Link>
        {(peut(user, "colis_creer") || peut(user, "colis_instance")) && <Button variant="outline" onClick={() => setAjout(true)} data-testid="btn-ajouter-colis"><ScanLine size={15} /> Scanner un colis</Button>}
      </div>

      {introuvable ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">Ce colis n'existe pas (ou a été supprimé).</div>
      ) : !c ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px] items-start" data-testid="colis-fiche">
          <section className="rounded-xl border border-border bg-card">
            <header className="p-5 border-b border-border flex flex-wrap items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Package size={22} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Colis{c.type_label ? ` · ${c.type_label}` : ""}</p>
                <p className="font-mono text-2xl sm:text-3xl font-bold tracking-wide leading-tight break-all" data-testid="colis-fiche-numero">{numeroLisible(c.numero)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatutColis c={c} />
                  <button onClick={copier} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Copy size={12} /> Copier</button>
                  {suivi && <a href={suivi} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink size={12} /> Suivi {c.transporteur}</a>}
                </div>
              </div>
            </header>
            <dl className="px-5 py-2">
              <Ligne label="N° de colis"><span className="font-mono">{c.numero}</span></Ligne>
              <Ligne label="Type">{c.type_label || <span className="text-muted-foreground">Non reconnu</span>}</Ligne>
              {c.etiquette?.expedition && <Ligne label="N° d'expédition"><span className="font-mono">{c.etiquette.expedition}</span> <span className="text-muted-foreground">· pièce {c.etiquette.piece}</span></Ligne>}
              <Ligne label="Saisie">
                {c.source === "scan" ? <span className="inline-flex items-center gap-1"><ScanLine size={13} /> {c.source_label}</span> : c.source_label}
              </Ligne>
              {c.code_barre && c.code_barre !== c.numero && !c.etiquette?.destinataire && c.code_barre.length <= 40 && <Ligne label="Code-barres lu"><span className="font-mono text-xs break-all">{c.code_barre}</span></Ligne>}
              {c.format_code && <Ligne label="Type de code">{c.format_code}</Ligne>}
              <Ligne label="Créé le">{fmtDateHeure(c.created_at)}</Ligne>
              <Ligne label="Créé par">{c.created_by?.nom || "—"} <span className="text-muted-foreground">(matricule {c.created_by?.matricule || "—"})</span></Ligne>
            </dl>
            {c.instance_en_cours && (
              <div className="mx-5 mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex items-center gap-2">
                <AlertOctagon size={15} className="text-amber-500 shrink-0" />
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>En instance : <b>{c.instance_en_cours}</b>{c.instances_ouvertes > 1 && <span className="text-muted-foreground"> (+{c.instances_ouvertes - 1} autre{c.instances_ouvertes > 2 ? "s" : ""})</span>}</span>
                  <EcheanceBadge e={c.echeance} testId="fiche-echeance" />
                </span>
              </div>
            )}
            {peut(user, "colis_supprimer") && (
              <div className="px-5 pb-5 pt-1">
                {confirmDel
                  ? <div className="flex gap-2"><Button size="sm" variant="destructive" onClick={supprimer} data-testid="colis-supprimer-ok">Supprimer la fiche ?</Button><Button size="sm" variant="ghost" onClick={() => setConfirmDel(false)}>Annuler</Button></div>
                  : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(true)}><Trash2 size={14} /> Supprimer la fiche</Button>}
              </div>
            )}
          </section>

          <div className="space-y-5 lg:row-span-3 order-last lg:order-none">
          <SuiviFichier c={c} />
          {c.etiquette?.destinataire && (
            <section className="rounded-xl border border-border bg-card p-5" data-testid="colis-etiquette">
              <h2 className="font-heading font-semibold">Lu sur l'étiquette</h2>
              <p className="text-xs text-muted-foreground mb-3">Informations du code-barres 2D du transporteur.</p>
              <dl className="text-sm space-y-2">
                {c.etiquette.destinataire && <div><dt className="text-xs text-muted-foreground">Destinataire</dt><dd className="font-semibold">{c.etiquette.destinataire}</dd></div>}
                {(c.etiquette.adresse || c.etiquette.ville) && (
                  <div><dt className="text-xs text-muted-foreground">Adresse</dt>
                    <dd>{c.etiquette.adresse}{c.etiquette.adresse && <br />}{[c.etiquette.cp, c.etiquette.ville].filter(Boolean).join(" ")}{c.etiquette.pays ? ` (${c.etiquette.pays})` : ""}</dd></div>
                )}
                {(c.etiquette.poids || c.etiquette.colis) && (
                  <div><dt className="text-xs text-muted-foreground">Poids / colis</dt><dd>{[c.etiquette.poids, c.etiquette.colis && `colis ${c.etiquette.colis}`].filter(Boolean).join(" · ")}</dd></div>
                )}
              </dl>
            </section>
          )}
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-heading font-semibold mb-3">Historique</h2>
            <ol className="relative border-l border-border ml-1.5 space-y-4">
              {(c.historique || []).slice().reverse().map((e, i) => (
                <li key={i} className="pl-4">
                  <span className="absolute -left-1.5 w-3 h-3 rounded-full border-2 border-card bg-primary" />
                  <p className="text-sm font-semibold">{e.label}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateHeure(e.date)} · {e.par?.nom || "—"} ({e.par?.matricule || "—"})</p>
                </li>
              ))}
            </ol>
          </section>
          </div>

          <section className="rounded-xl border border-border bg-card p-5" data-testid="colis-instances">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <h2 className="font-heading font-semibold">Instances</h2>
                <p className="text-xs text-muted-foreground">Absent, en congés, colis cassé, adresse introuvable…</p>
              </div>
              {peut(user, "colis_instance") && <Button size="sm" onClick={() => setInstanceOpen(true)} data-testid="btn-ajouter-instance"><Plus size={14} /> Ajouter une instance</Button>}
            </div>
            {c.instances.length === 0 ? <p className="text-sm text-muted-foreground">Aucune instance.</p> : (
              <ul className="space-y-2">
                {c.instances.slice().reverse().map((inst) => (
                  <InstanceCarte key={inst.id} colis={c} inst={inst} user={user} onChange={recharger}
                    photos={c.photos.filter((p) => p.instance_id === inst.id)} />
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-border bg-card p-5" data-testid="colis-photos">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <h2 className="font-heading font-semibold">Photos <span className="text-muted-foreground font-normal text-sm">({c.photos.length})</span></h2>
                <p className="text-xs text-muted-foreground">Toutes les photos du colis, y compris celles des instances.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!peut(user, "colis_photos") ? null : envoi ? (
                  <span className="inline-flex items-center gap-2 h-8 px-3 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Envoi {envoi}…</span>
                ) : (
                  <>
                    <label className="inline-flex">
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={ajouterPhotos} data-testid="fiche-photo-camera" />
                      <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-accent"><Camera size={14} /> Prendre une photo</span>
                    </label>
                    <label className="inline-flex">
                      <input type="file" accept="image/*" multiple className="hidden" onChange={ajouterPhotos} data-testid="fiche-photo-galerie" />
                      <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-accent"><ImagePlus size={14} /> Galerie</span>
                    </label>
                  </>
                )}
              </div>
            </div>
            {c.photos.length === 0 ? <p className="text-sm text-muted-foreground">Aucune photo.</p>
              : <PhotosGrille colis={c} photos={c.photos} user={user} onChange={() => recharger()} />}
          </section>
        </div>
      )}
      {c && <InstanceDialog open={instanceOpen} colis={c} motifs={motifs} onClose={() => setInstanceOpen(false)} onSaved={(d) => { setInstanceOpen(false); setC(d); }} />}
      <AjoutColisDialog open={!!ajout} startScan codeInitial={codeLu} onClose={() => { setAjout(false); setCodeLu(null); }} onCreated={(d) => { if (d.id === c?.id) setC(d); }} />
    </AppShell>
  );
}
